import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import AuthForm from '../components/Auth/AuthForm';
import { useAuth } from '../contexts/AuthContext';
import { Paper, Button, Alert, Text } from '@mantine/core';
import { IconLock, IconMail } from '@tabler/icons-react';
import elegantBallerina from '../assets/artistic_ballerina_shot.jpg';
import pointeShoeIcon from '../assets/pointe_shoe_icon.png';
import peopleIcon from '../assets/people_icon.png';
import { getActiveSeason, getSeasonState, getNextRegistration, isRegistrationOpenForUser, CONTACT_EMAIL } from '../config/seasons';

const Register = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const activeSeason = getActiveSeason();
  const seasonOpen = isRegistrationOpenForUser(activeSeason.year, user?.email);
  // Closed means either "this season hasn't opened yet" or "it's over".
  const comingSoon = getSeasonState(activeSeason.year) === 'coming_soon';
  const nextRegistration = getNextRegistration();

  return (
    <Layout
      title="Register for Competition | Prix du Canada Ballet"
      description="Register for Prix du Canada ballet competition in Toronto. Create your account to access registration, workshops, and exclusive dancer resources."
      keywords="ballet competition registration, Prix du Canada signup, dance competition Toronto, ballet registration Canada"
    >

      {/* Elegant Hero Section */}
      <section className="elegant-hero">
        <div className="hero-background">
          <img src={elegantBallerina} alt="Elegant ballet dancer" style={{opacity: 0.08}} />
        </div>
        <div className="container">
          <h1>Register for Prix du Canada</h1>
          <p style={{textAlign: 'center', fontSize: '1.3rem', maxWidth: '700px', margin: '0 auto'}}>
            {!seasonOpen
              ? comingSoon
                ? `Registration for Prix du Canada ${activeSeason.year} opens ${nextRegistration.opensPhrase}.`
                : `Registration for Prix du Canada ${activeSeason.year} is now closed. Registration for ${nextRegistration.year} opens ${nextRegistration.opensPhrase}.`
              : user
                ? `Complete your registration for Prix du Canada ${activeSeason.year}`
                : 'Your journey to the professional stage begins here. Create your account to access registration, exclusive workshops, and join Canada\'s most inclusive ballet competition.'}
          </p>
        </div>
      </section>

      {!seasonOpen ? (
        <section className="section" style={{background: 'var(--light-gray)', paddingTop: '4rem', paddingBottom: '4rem'}}>
          <div className="container" style={{ maxWidth: '640px' }}>
            <Alert
              icon={<IconLock size={18} />}
              color="yellow"
              variant="light"
              title={comingSoon ? `Registration for ${activeSeason.year} opens soon` : `Registration for ${activeSeason.year} is closed`}
              mb="lg"
            >
              {!comingSoon && (
                <Text size="sm" mb="sm">
                  Thank you for your interest in Prix du Canada. Registration for {activeSeason.year} has closed
                  and the season is complete.
                </Text>
              )}
              <Text size="sm">
                <strong>Registration for {nextRegistration.year} opens {nextRegistration.opensPhrase}.</strong>{' '}
                Subscribe on our <a href="/#newsletter" style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>home page</a>{' '}
                to be notified first — early-bird pricing and priority spots go quickly.
              </Text>
            </Alert>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {user ? (
                <Button
                  size="md"
                  onClick={() => navigate('/profile')}
                  style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
                >
                  View my profile
                </Button>
              ) : (
                <Button size="md" onClick={() => navigate('/login')} variant="light">
                  Sign in
                </Button>
              )}
              <Button
                component="a"
                href={`mailto:${CONTACT_EMAIL}`}
                leftSection={<IconMail size={16} />}
                variant="outline"
              >
                Contact us
              </Button>
            </div>
          </div>
        </section>
      ) : user ? (
        // If user is logged in, show registration type selection
        <section className="section" style={{background: 'var(--light-gray)', paddingTop: '4rem', paddingBottom: '4rem'}}>
          <div className="container">
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <h2 style={{textAlign: 'center', marginBottom: '3rem', fontFamily: "'Playfair Display', 'Georgia', serif", color: 'var(--charcoal)'}}>
                Who are you registering?
              </h2>

                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem'}}>
                  {/* Individual Dancer */}
                  <Paper
                  radius="md"
                  p="xl"
                  withBorder
                  style={{
                    borderTop: '4px solid var(--primary-gold)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    ':hover': { transform: 'translateY(-4px)' },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                    borderRadius: '50%',
                    margin: '0 auto 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    <img src={pointeShoeIcon} alt="Ballet dancer" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                  </div>

                  <h3 style={{
                    fontFamily: "'Playfair Display', 'Georgia', serif",
                    fontSize: '1.5rem',
                    marginBottom: '1rem',
                    color: 'var(--charcoal)'
                  }}>
                    Individual Dancer
                  </h3>

                  <p style={{ color: 'var(--soft-gray)', marginBottom: '1.5rem', lineHeight: 1.6, flexGrow: 1 }}>
                    I am registering myself as a solo dancer for the competition
                  </p>

                  <Button
                    fullWidth
                    onClick={() => navigate('/register/individual/step-1')}
                    style={{
                      background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                      fontWeight: 600
                    }}
                  >
                    Register as Individual
                  </Button>
                </Paper>

                {/* School/Teacher */}
                <Paper
                  radius="md"
                  p="xl"
                  withBorder
                  style={{
                    borderTop: '4px solid var(--primary-gold)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                  onClick={() => navigate('/register/school')}
                >
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                    borderRadius: '50%',
                    margin: '0 auto 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    <img src={peopleIcon} alt="School/Teacher" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                  </div>

                  <h3 style={{
                    fontFamily: "'Playfair Display', 'Georgia', serif",
                    fontSize: '1.5rem',
                    marginBottom: '1rem',
                    color: 'var(--charcoal)'
                  }}>
                    School / Teacher
                  </h3>

                  <p style={{ color: 'var(--soft-gray)', marginBottom: '1.5rem', lineHeight: 1.6, flexGrow: 1 }}>
                    I am a teacher or school registering my students for the competition
                  </p>

                  <Button
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/register/school');
                    }}
                    style={{
                      background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                      fontWeight: 600
                    }}
                  >
                    Register as School
                  </Button>
                </Paper>
              </div>
            </div>
          </div>
        </section>
      ) : (
        // If user is not logged in, show auth form and benefits
        <>
          {/* Registration Form Section */}
          <section className="section" style={{background: 'var(--light-gray)', paddingTop: '4rem', paddingBottom: '4rem'}}>
            <div className="container">
              <AuthForm
                defaultTab="signup"
                title="Create Your Account"
                subtitle="Join Prix du Canada and take the first step toward the stage"
              />
            </div>
          </section>

          {/* Benefits Section */}
          <section className="section" style={{background: 'var(--warm-white)', padding: '3rem 0'}}>
            <div className="container">
              <div style={{maxWidth: '900px', margin: '0 auto'}}>
                <h2 style={{textAlign: 'center', marginBottom: '2rem'}}>Why Create an Account?</h2>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginBottom: '2rem'}}>
                  <div style={{textAlign: 'center'}}>
                    <div style={{width: '60px', height: '60px', background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))', borderRadius: '50%', margin: '0 auto 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <span style={{color: 'white', fontSize: '1.5rem'}}>🎭</span>
                    </div>
                    <h3 style={{fontSize: '1.1rem', marginBottom: '0.5rem'}}>Sign Up for Competition</h3>
                    <p style={{color: 'var(--soft-gray)', fontSize: '0.95rem'}}>Be ready the moment registration opens {nextRegistration.opensPhrase}</p>
                  </div>
                  <div style={{textAlign: 'center'}}>
                    <div style={{width: '60px', height: '60px', background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))', borderRadius: '50%', margin: '0 auto 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <span style={{color: 'white', fontSize: '1.5rem'}}>✨</span>
                    </div>
                    <h3 style={{fontSize: '1.1rem', marginBottom: '0.5rem'}}>Exclusive Workshops</h3>
                    <p style={{color: 'var(--soft-gray)', fontSize: '0.95rem'}}>Access masterclasses and training</p>
                  </div>
                  <div style={{textAlign: 'center'}}>
                    <div style={{width: '60px', height: '60px', background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))', borderRadius: '50%', margin: '0 auto 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <span style={{color: 'white', fontSize: '1.5rem'}}>📋</span>
                    </div>
                    <h3 style={{fontSize: '1.1rem', marginBottom: '0.5rem'}}>Manage Everything</h3>
                    <p style={{color: 'var(--soft-gray)', fontSize: '0.95rem'}}>Pick up where you left off in one place</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      
      {/* School Registration Access Modal */}
    </Layout>
  );
};

export default Register; 